import React, { useState, useEffect } from 'react';
import './QtyControl.css';

interface QtyControlProps {
  defaultValue?: number;
  min?: number;
  max?: number;
  onChange?: (value: number) => void;
}

const QtyControl: React.FC<QtyControlProps> = ({
  defaultValue = 1,
  min = 1,
  max = 99,
  onChange,
}) => {
  const [qty, setQty] = useState(defaultValue);
  
  // Update internal state when defaultValue changes
  useEffect(() => {
    setQty(defaultValue);
  }, [defaultValue]);

  const handleChange = (value: number) => {
    const clampedValue = Math.max(min, Math.min(max, value));
    setQty(clampedValue);
    onChange?.(clampedValue);
  };

  const parseInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    Number(e.target.value.replace(/[^0-9]/g, '')) || min;

  // A labelled 44px field, not a spinner box: muted "Qty" prefix inside the
  // field, the value, then a stacked stepper pair on the right. Violet is a
  // focus/hover accent only — the resting border is the shared field stroke.
  return (
    <div className="qty-control">
      <span className="qty-label">Qty</span>
      <input
        type="text"
        className="qty-value"
        aria-label="Quantity"
        style={{ width: `${qty.toString().length}ch` }}
        value={qty}
        onChange={(e) => handleChange(parseInput(e))}
      />
      <div className="qty-steppers">
        <button
          type="button"
          className="qty-step"
          aria-label="Increase quantity"
          onClick={() => handleChange(qty + 1)}
        >
          <svg width="7" height="5" viewBox="0 0 7 5" fill="none" aria-hidden="true">
            <path d="M1 4L3.5 1.5L6 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          className="qty-step"
          aria-label="Decrease quantity"
          onClick={() => handleChange(qty - 1)}
        >
          <svg width="7" height="5" viewBox="0 0 7 5" fill="none" aria-hidden="true">
            <path d="M1 1L3.5 3.5L6 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default QtyControl;
